// Generated from scripts/product-history.ts. Rebuild with pnpm scripts:build.
/**
 * The products a shopper looked at on this device, and the section that lists them.
 *
 * - The history is a list of `{ id, handle }`, newest first, one entry per product, at most 12. It lives in
 *   the browser's localStorage under `wontio-product-history`. Nothing else is stored: no title, price or
 *   image, since Shopify renders those fresh for the shopper's market. The storage, its validation and the
 *   in-memory fallback for blocked or full storage belong to the stored list owner (`stored-list.ts`).
 * - `<product-history>` records the product of the page it sits on. The layout renders it on product pages
 *   only (`snippets/product-history-tracker.liquid`), so a card hover, a quick view and a prefetch never
 *   record. The theme editor never records.
 * - `<recently-viewed>` is the behavior of `sections/recently-viewed.liquid`. The section renders empty and
 *   collapsed, because the history exists only in the browser. On connect this element reads the history
 *   synchronously, leaves the section collapsed when there is nothing to show, and otherwise reveals it with
 *   placeholder cards of the final size and fills it through `<product-id-list>`. When no card comes back it
 *   collapses again. Its Clear button empties the history, announces the result and collapses the section.
 *
 * `read`, `record` and `clear` are the history's public API. Another module that needs them imports this
 * module through the import map, which gets its entry when the first such module appears.
 */
import { getScrollTop, scrollTo as scrollPage } from '@theme/scroll-container';
import { isProductIdItem } from '@theme/product-id';
import { createStoredList } from '@theme/stored-list';
import { onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
export const HISTORY_LIMIT = 12;
/** The stored list, which keeps the history on this device and in memory when storage fails (scripts/stored-list.ts). */
const history = createStoredList({
    storage: 'local',
    key: 'wontio-product-history',
    limit: HISTORY_LIMIT,
    parseEntry: (value) => (isProductIdItem(value) ? { id: value.id, handle: value.handle } : undefined),
});
/** The history, newest first. */
export function read() {
    return history.read();
}
/** Puts `entry` first, drops any older entry of the same product and keeps the newest 12. Returns the new history. */
export function record(entry) {
    return history.write([{ id: entry.id, handle: entry.handle }, ...history.read().filter((existing) => existing.id !== entry.id)]);
}
/** Empties the history. */
export function clear() {
    history.clear();
}
function inDesignMode() {
    const shopify = Reflect.get(window, 'Shopify');
    return typeof shopify === 'object' && shopify !== null && 'designMode' in shopify && shopify.designMode === true;
}
function isAbort(error) {
    return error instanceof DOMException && error.name === 'AbortError';
}
/** Records the product page it sits on. Attributes: `data-product-id` and `data-product-handle`. */
class ProductHistory extends HTMLElement {
    connectedCallback() {
        if (inDesignMode())
            return;
        const entry = { id: this.dataset.productId, handle: this.dataset.productHandle };
        if (isProductIdItem(entry))
            record(entry);
    }
}
function isProductIdList(value) {
    return (value instanceof HTMLElement &&
        'render' in value &&
        typeof value.render === 'function' &&
        'renderPlaceholders' in value &&
        typeof value.renderPlaceholders === 'function' &&
        'abort' in value &&
        typeof value.abort === 'function' &&
        'clear' in value &&
        typeof value.clear === 'function' &&
        'maximum' in value &&
        typeof value.maximum === 'number');
}
/** Runs `change`, which resizes `content`, and keeps the page where it was when `host` sits above the viewport (Safari has no scroll anchoring). */
function keepReadingPosition(host, content, change) {
    const above = host.getBoundingClientRect().bottom <= 0;
    const before = content.getBoundingClientRect().height;
    change();
    if (!above)
        return;
    const delta = content.getBoundingClientRect().height - before;
    if (delta !== 0)
        scrollPage({ top: getScrollTop() + delta, behavior: 'instant' });
}
/**
 * The recently viewed products section. Markup contract (`sections/recently-viewed.liquid`):
 *
 * - `[data-recently-viewed-content]` holds everything the shopper sees, `hidden` until there is something to show.
 * - `[data-recently-viewed-status]` is the polite live region. It sits outside the content so the result of
 *   Clear is announced after the content has gone.
 * - `<product-id-list>` inside the content is filled with the products.
 * - `[data-recently-viewed-clear]` is the optional Clear button.
 * - `data-current-product-id` is the product of the page, which the list leaves out. `data-cleared-message`
 *   is the announcement. `data-placeholder-count` is how many placeholder cards the theme editor shows.
 *   `data-design-mode` marks the theme editor.
 *
 * Giving the host `tabindex="-1"` lets focus move to it when Clear removes the focused button.
 */
class RecentlyViewed extends HTMLElement {
    #lifetime;
    /** The ids the section last showed, so a page restored from the back-forward cache refreshes only when the history changed. */
    #shown = '';
    /** Counts the runs of `#show` and the presses of Clear, so a run that something newer has replaced stops. */
    #epoch = 0;
    connectedCallback() {
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        const { signal } = lifetime;
        this.querySelector('[data-recently-viewed-clear]')?.addEventListener('click', this.#onClear, { signal });
        window.addEventListener('pageshow', (event) => {
            if (event.persisted)
                void this.#show(signal);
        }, { signal });
        void this.#show(signal);
    }
    disconnectedCallback() {
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        this.#list?.abort();
    }
    get #designMode() {
        return this.hasAttribute('data-design-mode');
    }
    get #content() {
        return this.querySelector('[data-recently-viewed-content]');
    }
    /** The card list, once its module has defined it. Before that it is a plain element without methods. */
    get #list() {
        const list = this.querySelector('product-id-list');
        return isProductIdList(list) ? list : undefined;
    }
    async #show(signal) {
        const content = this.#content;
        if (!content || !this.querySelector('product-id-list'))
            return;
        const items = read().filter((entry) => entry.id !== this.dataset.currentProductId);
        const placeholders = items.length === 0 && this.#designMode;
        const shown = placeholders ? 'placeholders' : items.map((entry) => entry.id).join(',');
        if (shown === this.#shown && !content.hidden)
            return;
        this.#shown = shown;
        const epoch = ++this.#epoch;
        if (items.length === 0 && !placeholders) {
            this.#setCollapsed(true);
            this.#list?.clear();
            return;
        }
        // The card renderer loads after the markup; until it exists the section stays collapsed.
        await customElements.whenDefined('product-id-list');
        const list = this.#list;
        if (signal.aborted || epoch !== this.#epoch || !list)
            return;
        this.#setCollapsed(false);
        try {
            if (placeholders) {
                const count = Number.parseInt(this.dataset.placeholderCount ?? '', 10);
                await list.renderPlaceholders(Number.isNaN(count) ? list.maximum : count, this.dataset.placeholderHandle);
            }
            else if ((await list.render(items)).count === 0 && epoch === this.#epoch) {
                await this.#remove();
            }
        }
        catch (error) {
            if (isAbort(error) || signal.aborted || epoch !== this.#epoch)
                return;
            await this.#remove();
        }
    }
    #setCollapsed(collapsed) {
        const content = this.#content;
        if (!content || content.hidden === collapsed)
            return;
        keepReadingPosition(this, content, () => {
            content.hidden = collapsed;
        });
    }
    /** Fades the content out when the shopper can see it, under the motion guide's exit timing, and hides it. */
    async #collapse() {
        const content = this.#content;
        if (!content || content.hidden)
            return;
        const bounds = this.getBoundingClientRect();
        const visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
        if (visible && !prefersReducedMotion()) {
            content.setAttribute('data-leaving', '');
            await onAnimationEnd(content, undefined, { subtree: false });
        }
        this.#setCollapsed(true);
        content.removeAttribute('data-leaving');
    }
    async #remove() {
        await this.#collapse();
        this.#shown = '';
        this.#list?.clear();
    }
    #onClear = () => {
        clear();
        this.#epoch += 1;
        this.#list?.abort();
        // The button is about to disappear with the content; the host keeps focus where the section was.
        this.focus({ preventScroll: true });
        const status = this.querySelector('[data-recently-viewed-status]');
        if (status)
            status.textContent = this.dataset.clearedMessage ?? '';
        void this.#remove();
    };
}
if (!customElements.get('product-history')) {
    customElements.define('product-history', ProductHistory);
}
if (!customElements.get('recently-viewed')) {
    customElements.define('recently-viewed', RecentlyViewed);
}
